// Media Accordion block
// ---------------------------------------------------------------------------
// Single-open accordion where selecting an item swaps a shared image via a
// crossfade. There is only ever ONE figure per block instance (two stacked
// <img> layers for the crossfade); that same figure node is relocated between
// the desktop media slot and the currently-active panel's mobile slot as the
// viewport crosses the 768px breakpoint — never duplicated per panel.

function initMediaAccordion(section) {
    const triggers = Array.from(section.querySelectorAll(".accordion-trigger"));
    if (!triggers.length) return;

    const desktopSlot = section.querySelector(".media-accordion-media");
    // Mobile parents the figure above the dropdown (image → select → content).
    const mobileSlot = section.querySelector(".media-accordion-media-mobile");
    // Mobile-only control: the section titles collapse into this dropdown (CSS
    // shows it only below the breakpoint). Its options' values are trigger ids.
    const select = section.querySelector(".accordion-select");
    const mq = window.matchMedia("(max-width: 767px)");
    const reducedMq = window.matchMedia("(prefers-reduced-motion: reduce)");

    // The shared crossfade figure. It's normally server-rendered into the
    // desktop slot (first image already active) so the browser can discover the
    // above-the-fold image during HTML parse — adopt that node when present.
    // Fall back to building it here if the server didn't render one.
    let figure = section.querySelector(".media-accordion-figure");
    let layerA;
    let layerB;
    let activeLayer = null;

    if (figure) {
        const layers = figure.querySelectorAll("img");
        layerA = layers[0];
        layerB = layers[1];
        // The server marks the visible layer with .is-active; treat it as the
        // starting active layer so showImage() early-returns without re-fetching.
        activeLayer = figure.querySelector("img.is-active") || null;
        // Drop the server's paint-suppression class now the JS owns transitions.
        figure.classList.remove("no-transition");
    } else {
        figure = document.createElement("div");
        figure.className = "media-accordion-figure";
        layerA = document.createElement("img");
        layerB = document.createElement("img");
        layerA.alt = "";
        layerB.alt = "";
        // Only the visible layer is exposed to assistive tech — the image is real
        // content (meaningful per-item alt), but we don't want the fading-out
        // layer double-announcing. swap() flips these.
        layerA.setAttribute("aria-hidden", "true");
        layerB.setAttribute("aria-hidden", "true");
        figure.append(layerA, layerB);
    }

    function showImage(src, alt, animate) {
        // Already showing this exact source — just keep alt fresh.
        if (activeLayer && activeLayer.getAttribute("src") === src) {
            activeLayer.alt = alt;
            return;
        }

        const incoming = activeLayer === layerA ? layerB : layerA;
        const outgoing = activeLayer;
        incoming.src = src;
        incoming.alt = alt;

        // Incoming fades in ON TOP of the still-opaque outgoing layer, so there's
        // never a see-through midpoint (no cross-dissolve dip).
        incoming.style.zIndex = "2";
        if (outgoing) outgoing.style.zIndex = "1";

        const commit = () => {
            incoming.setAttribute("aria-hidden", "false");
            if (outgoing) outgoing.setAttribute("aria-hidden", "true");
            activeLayer = incoming;
        };

        if (!animate || reducedMq.matches) {
            // Skip the fade (initial paint / reduced motion).
            figure.classList.add("no-transition");
            incoming.classList.add("is-active");
            if (outgoing) outgoing.classList.remove("is-active");
            void figure.offsetWidth; // force reflow so the class takes effect
            figure.classList.remove("no-transition");
            commit();
            return;
        }

        // Reset the incoming layer to transparent instantly (it may have been the
        // previously active layer), without animating that reset.
        figure.classList.add("no-transition");
        incoming.classList.remove("is-active");
        void figure.offsetWidth;
        figure.classList.remove("no-transition");

        const reveal = () => {
            // Next frame so the opacity:0 starting point is committed first.
            requestAnimationFrame(() => incoming.classList.add("is-active"));
            commit();
        };

        // Decode the new image before fading so we never fade in a half-loaded
        // frame that then pops.
        if (typeof incoming.decode === "function") {
            incoming.decode().then(reveal).catch(reveal);
        } else {
            reveal();
        }
    }

    function placeImage(trigger, animate) {
        const src = trigger.dataset.image || "";
        const alt = trigger.dataset.alt || "";

        if (!src) {
            // Item has no image — take the figure out of the flow entirely.
            figure.remove();
            return;
        }

        // Relocate first (and only when the parent actually changes) so a DOM
        // move never interrupts an in-flight fade. Mobile parents the figure in
        // the slot above the dropdown; desktop in the sticky side column.
        const target = mq.matches ? mobileSlot : desktopSlot;
        if (target && figure.parentNode !== target) target.appendChild(figure);

        showImage(src, alt, animate);
    }

    function setActive(trigger, animate) {
        triggers.forEach((t) => {
            const panel = document.getElementById(
                t.getAttribute("aria-controls"),
            );
            const on = t === trigger;
            t.setAttribute("aria-expanded", String(on));
            if (panel) panel.hidden = !on;
        });
        // Keep the mobile dropdown in sync (initial paint, desktop clicks,
        // breakpoint crossing) so both controls always agree on the active item.
        if (select && trigger.id) select.value = trigger.id;
        placeImage(trigger, animate);
    }

    // Scroll `el` to just below any sticky/fixed site header. On mobile we bring
    // the tapped item into view (its image renders inside the panel); on desktop
    // we bring the whole block into view (the image lives in the sticky column).
    function scrollElementToTop(el) {
        const header = document.getElementById("site-header");
        const pos = header ? getComputedStyle(header).position : "";
        const headerH =
            header && (pos === "sticky" || pos === "fixed")
                ? header.offsetHeight
                : 0;
        const top =
            window.scrollY + el.getBoundingClientRect().top - headerH - 8;
        window.scrollTo({ top, behavior: reducedMq.matches ? "auto" : "smooth" });
    }

    triggers.forEach((trigger) => {
        trigger.addEventListener("click", () => {
            setActive(trigger, true);
            const target = mq.matches
                ? trigger.closest(".accordion-item") || trigger // mobile: this item
                : section; // desktop: whole block
            scrollElementToTop(target);
        });
    });

    // Mobile dropdown drives the same active-item logic, then jumps up to the
    // top of the image so the chosen section is framed from its image down.
    if (select) {
        select.addEventListener("change", () => {
            const trigger = document.getElementById(select.value);
            if (!trigger) return;
            setActive(trigger, true);
            if (mobileSlot) scrollElementToTop(mobileSlot);
        });
    }

    // Relocate the image when the viewport crosses the breakpoint so an open
    // panel's image ends up in the right slot without a re-click.
    mq.addEventListener("change", () => {
        const active = section.querySelector(
            '.accordion-trigger[aria-expanded="true"]',
        );
        if (active) placeImage(active, false);
    });

    // Establish initial state from whichever trigger the server rendered open
    // (falls back to the first). No fade on first paint.
    const initial =
        section.querySelector('.accordion-trigger[aria-expanded="true"]') ||
        triggers[0];
    setActive(initial, false);
}

function initMediaAccordions() {
    document
        .querySelectorAll(".wp-block-pulp-media-accordion")
        .forEach(initMediaAccordion);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMediaAccordions);
} else {
    initMediaAccordions();
}
